import React, { useEffect, useState } from 'react';
import { View, Text, Platform } from 'react-native';
import { VictoryPie } from 'victory-native';
import { G, Line, Text as SvgText } from 'react-native-svg';
import Legend from './Legend';
import ChartStyles from './chartStyles';

interface PieChartProps {
  data: { name: string; value: number; color: string }[];
  donutRadius?: number;
  containerWidth?: number;
  legendFontColor?: string;
  size?: number;
}

// Padding carves space inside the SVG for labels outside the pie ring.
// outerRadius = (pieSize − 2 × PIE_PADDING) / 2
const PIE_PADDING = 44;
const DEFAULT_PIE_SIZE = Platform.OS === 'web' ? 240 : 260;

interface SliceLabelProps {
  x?: number;
  y?: number;
  datum?: { x: string; y: number };
  total?: number;
  outerRadius?: number;
  pieSize?: number;
}

function SliceLabel({
  x = 0,
  y = 0,
  datum,
  total = 1,
  outerRadius = 0,
  pieSize = DEFAULT_PIE_SIZE,
}: SliceLabelProps) {
  if (!datum || datum.y === 0 || total === 0) return null;
  const pct = Math.round((datum.y / total) * 100);
  if (pct === 0) return null;

  const cx = pieSize / 2;
  const cy = pieSize / 2;

  // Derive the slice midpoint angle from Victory's calculated label position
  const angle = Math.atan2(y - cy, x - cx);

  // Line starts at the outer edge of the pie ring
  const edgeX = cx + outerRadius * Math.cos(angle);
  const edgeY = cy + outerRadius * Math.sin(angle);

  const isRight = x >= cx;

  return (
    <G>
      <Line
        x1={edgeX} y1={edgeY}
        x2={x}     y2={y}
        stroke="#bbb"
        strokeWidth={1.2}
      />
      <SvgText
        x={x + (isRight ? 4 : -4)}
        y={y + 4}
        textAnchor={isRight ? 'start' : 'end'}
        fontSize={11}
        fill="#444"
        fontFamily="Inter, system-ui, sans-serif"
        fontWeight="600"
      >
        {`${pct}%`}
      </SvgText>
    </G>
  );
}

export default function FencelinePieChartComp(props: PieChartProps) {
  const [pieData, setPieData] = useState<{ x: string; y: number }[]>([]);
  const [colors, setColors] = useState<string[]>([]);

  useEffect(() => {
    if (props.data && props.data.length > 0) {
      setPieData(props.data.map(item => ({ x: item.name, y: item.value })));
      setColors(props.data.map(item => item.color));
    } else {
      setPieData([]);
    }
  }, [props.data]);

  const hasData = pieData.length > 0;
  const pieSize = props.size ?? DEFAULT_PIE_SIZE;
  const innerRadius = props.donutRadius ?? 50;
  const outerRadius = (pieSize - 2 * PIE_PADDING) / 2;
  const labelRadius = outerRadius + 22;
  const total = props.data.reduce((sum, item) => sum + item.value, 0);

  return (
    <View style={{ alignItems: 'center', width: '100%' }}>
      {/* Pie + center label */}
      <View style={{ width: pieSize, height: pieSize }}>
        <VictoryPie
          animate={{ duration: 800 }}
          colorScale={hasData ? colors : ['#f0f0f0']}
          innerRadius={innerRadius}
          padding={PIE_PADDING}
          labelRadius={labelRadius}
          labels={({ datum }) => datum.x}
          labelComponent={
            <SliceLabel
              total={hasData ? total : 0}
              outerRadius={outerRadius}
              pieSize={pieSize}
            />
          }
          height={pieSize}
          width={pieSize}
          data={hasData ? pieData : [{ x: 'No Data', y: 1 }]}
        />
        {hasData && total > 0 && (
          <View style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            justifyContent: 'center', alignItems: 'center',
          }}>
            <Text style={{ fontSize: 20, fontWeight: 'bold', color: '#263238', fontFamily: 'Inter, system-ui, sans-serif' }}>
              {total}
            </Text>
            <Text style={{ fontSize: 10, color: '#78909C', fontFamily: 'Inter, system-ui, sans-serif' }}>
              total
            </Text>
          </View>
        )}
      </View>

      {/* Legend */}
      {hasData && (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', paddingHorizontal: 8, paddingTop: 4, paddingBottom: 6, gap: 6 }}>
          {props.data.map((item, index) => (
            <Legend
              key={index}
              fontColor={props.legendFontColor || ChartStyles.chartTitle.color}
              name={`${item.name}: ${item.value}`}
              color={item.color}
            />
          ))}
        </View>
      )}
    </View>
  );
}
